import { Injectable } from '@angular/core';
import { ResourceTimingEntry } from '../types/resource-timing-entry';
import { RedactionService } from '../../security/redaction.service';
import { RingBuffer } from '../storage/ring-buffer';

@Injectable({
  providedIn: 'root',
})
export class ResourceTimingCollector {
  private readonly entries = new RingBuffer<ResourceTimingEntry>(120);
  private started = false;
  private observer?: PerformanceObserver;

  constructor(private readonly redactionService: RedactionService) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.captureBufferedEntries();

    if (typeof PerformanceObserver === 'undefined' || !PerformanceObserver.supportedEntryTypes?.includes('resource')) {
      return;
    }

    this.observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType !== 'resource') {
          continue;
        }

        const resource = entry as PerformanceResourceTiming;
        if (!this.shouldCaptureResource(resource)) {
          continue;
        }

        this.entries.push({
          url: this.redactionService.redactUrl(resource.name),
          initiatorType: resource.initiatorType || 'unknown',
          duration: Math.round(resource.duration),
          transferSize: resource.transferSize || undefined,
          encodedBodySize: resource.encodedBodySize || undefined,
          decodedBodySize: resource.decodedBodySize || undefined,
          timestamp: new Date().toISOString(),
        });
      }
    });

    this.observer.observe({ type: 'resource' });
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.observer?.disconnect();
    this.observer = undefined;
  }

  clear(): void {
    this.entries.clear();
  }

  getEntries(): readonly ResourceTimingEntry[] {
    return this.entries.toArray();
  }

  private shouldCaptureResource(resource: PerformanceResourceTiming): boolean {
    const url = this.redactionService.redactUrl(resource.name);

    if (resource.initiatorType === 'fetch' || resource.initiatorType === 'xmlhttprequest') {
      return true;
    }

    if (resource.duration >= 200) {
      return true;
    }

    if (this.isDevelopmentNoise(url)) {
      return false;
    }

    return resource.duration >= 100;
  }

  private isDevelopmentNoise(url: string): boolean {
    return [
      '/@fs/',
      '/.angular/cache/',
      '/vite/deps/',
      '/node_modules/',
      'chunk-',
      '/assets/i18n/',
      'primeicons.woff2',
      'favicon',
      'logo',
    ].some((fragment) => url.includes(fragment));
  }

  private captureBufferedEntries(): void {
    for (const entry of performance.getEntriesByType('resource')) {
      if (entry.entryType !== 'resource') {
        continue;
      }

      const resource = entry as PerformanceResourceTiming;
      if (!this.shouldCaptureResource(resource)) {
        continue;
      }

      this.entries.push({
        url: this.redactionService.redactUrl(resource.name),
        initiatorType: resource.initiatorType || 'unknown',
        duration: Math.round(resource.duration),
        transferSize: resource.transferSize || undefined,
        encodedBodySize: resource.encodedBodySize || undefined,
        decodedBodySize: resource.decodedBodySize || undefined,
        timestamp: new Date().toISOString(),
      });
    }
  }
}
